import React, { useState, useRef, useCallback, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { JsonataService } from '../services/jsonataService';
import { TokenService } from '../services/tokenService';

interface UploadedFile {
  id: string;
  name: string;
  content: any;
}

interface SchemaValidation {
  isValid: boolean;
  errors: any[] | null;
}

const JsonataMapping: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>('');
  const [jsonataExpression, setJsonataExpression] = useState<string>('');
  const [mappingResult, setMappingResult] = useState<any>(null);
  const [schemaValidation, setSchemaValidation] = useState<SchemaValidation | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string>('');
  const [autoExecuteEnabled, setAutoExecuteEnabled] = useState(true);
  const [isAutoExecuting, setIsAutoExecuting] = useState(false);
  const [isSuggestingMapping, setIsSuggestingMapping] = useState(false);
  const [suggestMappingProgress, setSuggestMappingProgress] = useState<string>('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: UploadedFile[] = [];
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = JSON.parse(e.target?.result as string);
          const newFile: UploadedFile = {
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            content: content
          };
          newFiles.push(newFile);

          if (newFiles.length === files.length) {
            setUploadedFiles(prev => [...prev, ...newFiles]);
            if (newFiles.length === 1) {
              setSelectedFileId(newFiles[0].id);
            }
          }
        } catch (error) {
          console.error('Error parsing JSON file:', error);
          setError(`Error parsing ${file.name}: Invalid JSON`);
        }
      };
      reader.readAsText(file);
    });
  };

  const handleSuggestMapping = async () => {
    if (uploadedFiles.length === 0) {
      setError('Please upload JSON files first');
      return;
    }

    setIsSuggestingMapping(true);
    setError('');
    setSuggestMappingProgress('Starting mapping suggestion...');

    try {
      const inputJsonFiles = uploadedFiles.map(file => file.content);

      // Create a custom suggestMapping function that can handle progress updates
      const suggestedMapping = await new Promise<string>((resolve, reject) => {
        const token = TokenService.getToken();
        if (!token) {
          reject(new Error('No access token found'));
          return;
        }

        fetch(`${process.env.REACT_APP_BACKEND_URL}/jsonata/suggest-mapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            inputJsonFiles
          })
        }).then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          const reader = res.body?.getReader();
          if (!reader) {
            throw new Error('No response body reader available');
          }

          const decoder = new TextDecoder();
          let buffer = '';

          const processStream = () => {
            reader.read().then(({ done, value }) => {
              if (done) {
                reject(new Error('Stream ended without final result'));
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));

                    if (data.final && data.suggestedMapping) {
                      console.log('✅ Mapping suggestion received successfully');
                      resolve(data.suggestedMapping);
                      return;
                    } else if (data.status) {
                      console.log(`📢 Progress: ${data.status}`);
                      setSuggestMappingProgress(data.status);
                    }
                  } catch (parseError) {
                    console.warn('Failed to parse SSE data:', line);
                  }
                }
              }

              processStream();
            }).catch(error => {
              console.error('❌ Error reading SSE stream:', error);
              reject(error);
            });
          };

          processStream();
        }).catch(error => {
          console.error('❌ Error in fetch request:', error);
          reject(error);
        });
      });

      setJsonataExpression(suggestedMapping);
      console.log('✅ Mapping suggestion applied successfully');
    } catch (err) {
      console.error('❌ Error suggesting mapping:', err);
      setError(err instanceof Error ? err.message : 'Failed to suggest mapping');
    } finally {
      setIsSuggestingMapping(false);
      setSuggestMappingProgress('');
    }
  };

  const handleExecuteMapping = useCallback(async () => {
    if (!selectedFileId || !jsonataExpression.trim()) {
      return;
    }

    const selectedFile = uploadedFiles.find(f => f.id === selectedFileId);
    if (!selectedFile) {
      setError('Selected file not found');
      return;
    }

    setIsExecuting(true);
    setError('');

    try {
      const data = await JsonataService.executeMapping({
        jsonataExpression: jsonataExpression,
        inputJson: selectedFile.content
      });

      setMappingResult(data.mappingResult);
      setSchemaValidation(data.schemaValidation);
    } catch (error) {
      console.error('Error executing mapping:', error);
      setError(`Error executing mapping: ${(error as Error).message}`);
    } finally {
      setIsExecuting(false);
    }
  }, [selectedFileId, jsonataExpression, uploadedFiles]);

  // Auto-execute mapping with debouncing
  useEffect(() => {
    if (!autoExecuteEnabled || !selectedFileId || !jsonataExpression.trim()) {
      return;
    }

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for auto-execution
    debounceTimeoutRef.current = setTimeout(() => {
      setIsAutoExecuting(true);
      handleExecuteMapping().finally(() => {
        setIsAutoExecuting(false);
      });
    }, 2000); // 2 seconds delay

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [jsonataExpression, selectedFileId, autoExecuteEnabled, handleExecuteMapping]);

  const selectedFile = uploadedFiles.find(f => f.id === selectedFileId);

  // Handle mouse events for resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const container = document.querySelector('.editor-container') as HTMLElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Limit the width between 20% and 80%
    const clampedWidth = Math.max(20, Math.min(80, newWidth));
    setLeftPanelWidth(clampedWidth);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="jsonata-mapping">
      <div className="jsonata-mapping-container">
        {/* Main JSONATA Section */}
        <div className="jsonata-main-card">
          {/* File Upload Section */}
          <div className="jsonata-field">
            <label htmlFor="file-upload">Upload JSON Files:</label>
            <div className="file-upload-container">
              <input
                ref={fileInputRef}
                id="file-upload"
                type="file"
                multiple
                accept=".json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <button
                className="upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                📁 Upload JSON Files
              </button>
              {uploadedFiles.length > 0 && (
                <button
                  className="suggest-btn"
                  onClick={handleSuggestMapping}
                  disabled={isSuggestingMapping}
                >
                  {isSuggestingMapping ? `🤖 ${suggestMappingProgress || 'Generating...'}` : '🤖 Suggest Mapping'}
                </button>
              )}
            </div>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="file-field">
              <label htmlFor="file-selector">Select Input File:</label>
              <select
                id="file-selector"
                value={selectedFileId}
                onChange={(e) => setSelectedFileId(e.target.value)}
                className="jsonata-dropdown"
              >
                {uploadedFiles.map(file => (
                  <option key={file.id} value={file.id}>
                    {file.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="error">
              ❌ {error}
            </div>
          )}

          {/* Schema Validation Status */}
          {schemaValidation && (
            <div className={`schema-validation ${schemaValidation.isValid ? 'valid' : 'invalid'}`}>
              {schemaValidation.isValid ? (
                <span>✅ Schema validation passed</span>
              ) : (
                <div>
                  <span>❌ Schema validation failed</span>
                  {schemaValidation.errors && (
                    <ul className="validation-errors">
                      {schemaValidation.errors.map((error: any, index: number) => (
                        <li key={index}>
                          {error.instancePath || 'root'}: {error.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Editor Section */}
          {uploadedFiles.length > 0 && (
            <div className="editor-container">
              {/* Left Editor - JSONATA Expression */}
              <div
                className="editor-panel left-panel"
                style={{ width: `${leftPanelWidth}%` }}
              >
                <div className="editor-header">
                  <h3>JSONATA Expression</h3>
                  <div className="editor-controls">
                    <label className="auto-execute-toggle">
                      <input
                        type="checkbox"
                        checked={autoExecuteEnabled}
                        onChange={(e) => setAutoExecuteEnabled(e.target.checked)}
                      />
                      <span>Auto-execute</span>
                    </label>
                    <button
                      className="execute-btn"
                      onClick={handleExecuteMapping}
                      disabled={isExecuting || !selectedFileId || !jsonataExpression.trim()}
                    >
                      {isExecuting ? '🔄 Executing...' : '▶️ Execute Mapping'}
                    </button>
                    {isAutoExecuting && (
                      <span className="auto-execute-indicator">⏳ Auto-executing...</span>
                    )}
                  </div>
                </div>
                <Editor
                  height="400px"
                  defaultLanguage="json"
                  value={jsonataExpression}
                  onChange={(value) => setJsonataExpression(value || '')}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineHeight: 1.5,
                    wordWrap: 'on',
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                    theme: 'vs-dark',
                    scrollbar: {
                      vertical: 'visible',
                      horizontal: 'visible',
                      verticalScrollbarSize: 8,
                      horizontalScrollbarSize: 8,
                    }
                  }}
                />
              </div>

              {/* Resizable Handle */}
              <div
                className={`resize-handle ${isDragging ? 'dragging' : ''}`}
                onMouseDown={handleMouseDown}
              >
                <div className="resize-handle-line"></div>
              </div>

              {/* Right Editor - Mapping Result */}
              <div
                className="editor-panel right-panel"
                style={{ width: `${100 - leftPanelWidth}%` }}
              >
                <div className="editor-header">
                  <h3>Mapping Result</h3>
                  <div className="editor-controls">
                    <div className="placeholder-controls"></div>
                  </div>
                </div>
                <Editor
                  height="400px"
                  defaultLanguage="json"
                  value={mappingResult ? JSON.stringify(mappingResult, null, 2) : ''}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineHeight: 1.5,
                    wordWrap: 'on',
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                    theme: 'vs-dark',
                    renderLineHighlight: 'none',
                    overviewRulerBorder: false,
                    hideCursorInOverviewRuler: true,
                    overviewRulerLanes: 0,
                    scrollbar: {
                      vertical: 'visible',
                      horizontal: 'visible',
                      verticalScrollbarSize: 8,
                      horizontalScrollbarSize: 8,
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Input Preview */}
          {selectedFile && (
            <div className="input-preview">
              <h3>Input Data Preview</h3>
              <Editor
                height="200px"
                defaultLanguage="json"
                value={JSON.stringify(selectedFile.content, null, 2)}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  fontSize: 12,
                  lineHeight: 1.5,
                  wordWrap: 'on',
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                  theme: 'vs-dark',
                  scrollbar: {
                    vertical: 'visible',
                    horizontal: 'visible',
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8,
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* JSONATA Mapping Layout - matching RulesetAudit */
        .jsonata-mapping {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          width: 100%;
          position: relative;
        }

        .jsonata-mapping-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 30px;
          width: 100%;
          margin: 0;
          min-height: 0;
          position: relative;
          padding-top: 0;
        }

        /* JSONATA Main Card - matching RulesetAudit */
        .jsonata-main-card {
          background: rgba(45, 74, 135, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 25px;
          backdrop-filter: blur(4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          margin-bottom: 30px;
        }

        .jsonata-field {
          margin-bottom: 20px;
          width: 100%;
        }

        .jsonata-field:last-child {
          margin-bottom: 0;
        }

        .file-field {
          width: 100%;
        }

        .jsonata-field label,
        .file-field label {
          display: block;
          margin-bottom: 8px;
          color: #ffffff;
          font-weight: 500;
          font-size: 0.95rem;
        }

        .file-upload-container {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .upload-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .upload-btn:hover {
          background: #5a6fd8;
          transform: translateY(-1px);
        }

        .suggest-btn {
          background: #28a745;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .suggest-btn:hover:not(:disabled) {
          background: #218838;
          transform: translateY(-1px);
        }

        .suggest-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .jsonata-dropdown {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #ffffff;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .jsonata-dropdown:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .jsonata-dropdown:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
          border: 1px solid #f5c6cb;
        }

        .schema-validation {
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .schema-validation.valid {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .schema-validation.invalid {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .validation-errors {
          margin: 10px 0 0 0;
          padding-left: 20px;
        }

        .validation-errors li {
          margin-bottom: 5px;
        }

        .editor-container {
          display: flex;
          gap: 0;
          margin-bottom: 20px;
          position: relative;
        }

        .editor-panel {
          background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
          border: 1px solid #3d5af1;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(45, 74, 135, 0.3);
          position: relative;
          flex-shrink: 0;
        }

        .editor-panel::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, #64c8ff, transparent);
          z-index: 1;
        }

        .left-panel {
          border-radius: 8px;
        }

        .right-panel {
          border-radius: 8px;
        }

        .editor-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 12px 16px;
          border-bottom: 1px solid #2d4a87;
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 56px;
        }

        .editor-header h3 {
          color: #64c8ff;
          font-size: 14px;
          font-weight: 500;
          margin: 0;
        }

        .editor-controls {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .auto-execute-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: #ffffff;
          cursor: pointer;
        }

        .auto-execute-toggle input[type="checkbox"] {
          margin: 0;
          cursor: pointer;
          accent-color: #667eea;
        }

        .execute-btn {
          background: none;
          border: 1px solid transparent;
          color: #b8c2d9;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .execute-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #2d4a87 0%, #1e3a5f 100%);
          border-color: #64c8ff;
          color: #ffffff;
          box-shadow: 0 1px 4px rgba(100, 200, 255, 0.2);
        }

        .execute-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: none;
          border-color: transparent;
        }

        .auto-execute-indicator {
          color: #667eea;
          font-size: 0.9rem;
          font-style: italic;
        }

        .placeholder-controls {
          width: 200px;
          height: 32px;
          display: flex;
          align-items: center;
        }

        /* Monaco Editor Dark Theme Override */
        .monaco-editor {
          background: #0d1117 !important;
        }

        .monaco-editor .monaco-editor-background {
          background: #0d1117 !important;
        }

        .monaco-editor .margin {
          background: #0d1117 !important;
        }

        .monaco-editor .monaco-editor-background,
        .monaco-editor .inputarea {
          background: #0d1117 !important;
        }

        /* Resize Handle */
        .resize-handle {
          width: 16px;
          background: rgba(255, 255, 255, 0.05);
          cursor: col-resize;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: background-color 0.2s ease;
          margin: 0 8px;
        }

        .resize-handle:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .resize-handle.dragging {
          background: rgba(102, 126, 234, 0.3);
        }

        .resize-handle-line {
          width: 2px;
          height: 40px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 1px;
        }

        .resize-handle:hover .resize-handle-line {
          background: rgba(255, 255, 255, 0.5);
        }

        .resize-handle.dragging .resize-handle-line {
          background: rgba(102, 126, 234, 0.8);
        }

        .input-preview {
          background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
          border: 1px solid #3d5af1;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(45, 74, 135, 0.3);
          position: relative;
        }

        .input-preview::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, #64c8ff, transparent);
          z-index: 1;
        }

        .input-preview h3 {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 8px 16px;
          border-bottom: 1px solid #2d4a87;
          color: #64c8ff;
          font-size: 14px;
          font-weight: 500;
          margin: 0;
        }

        /* Schema Validation - matching error styles */
        .schema-validation {
          background: rgba(45, 74, 135, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
          backdrop-filter: blur(4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          text-align: center;
          font-weight: 500;
          flex-shrink: 0;
        }

        .schema-validation.valid {
          border-color: rgba(76, 175, 80, 0.3);
          color: #4caf50;
        }

        .schema-validation.invalid {
          border-color: rgba(244, 67, 54, 0.3);
          color: #f44336;
        }

        .validation-errors {
          margin: 10px 0 0 0;
          padding-left: 20px;
          text-align: left;
        }

        .validation-errors li {
          margin-bottom: 5px;
          color: #f44336;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .jsonata-main-card {
            padding: 20px;
          }

          .jsonata-field {
            margin-bottom: 15px;
          }

          .editor-container {
            flex-direction: column;
            gap: 15px;
          }

          .left-panel,
          .right-panel {
            width: 100% !important;
            border-radius: 8px !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
          }

          .resize-handle {
            display: none;
          }

          .editor-controls {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
          }
        }

        @media (max-width: 480px) {
          .jsonata-main-card {
            padding: 15px;
          }

          .jsonata-field label,
          .file-field label {
            font-size: 0.9rem;
          }

          .upload-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default JsonataMapping; 